// @ts-nocheck
import { Quill } from 'react-quill-new'
import MagicUrl from 'quill-magic-url'
import assert from 'assert'
import BAD_TRANSLATION_WORDS from './badTranslations'
import { DeltaStatic } from 'react-quill-new'

const Embed = Quill.import('blots/embed')
const Inline = Quill.import('blots/inline')
const Delta = Quill.import('delta')

// BUG FIX: https://github.com/slab/quill/issues/1985
class FixedEmbed extends Embed {
  update(mutations, context) {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'childList' &&
        (Array.from(mutation.removedNodes).includes(this.leftGuard) ||
          Array.from(mutation.removedNodes).includes(this.rightGuard))
      ) {
        let tag
        if (mutation.previousSibling) {
          tag = mutation.previousSibling.innerText
        } else if (mutation.nextSibling) {
          tag = mutation.nextSibling.innerText
        }
        if (tag) {
          super.replaceWith('text', tag)
        }
      }
    })

    super.update(mutations, context)
  }
}

class MessageScript extends FixedEmbed {
  static create(value) {
    let node = super.create(value)
    node.setAttribute('class', 'message-script')
    node.setAttribute('contenteditable', 'false')
    node.setAttribute('spellcheck', 'false')
    node.setAttribute('autocomplete', 'off')
    node.setAttribute('autocorrect', 'off')
    node.setAttribute('autocapitalize', 'off')
    node.innerText = value
    return node
  }

  static value(node) {
    return node.innerText
  }
}

MessageScript.blotName = 'message-script'
MessageScript.tagName = 'span'
MessageScript.className = 'message-script'
Quill.register(MessageScript)

class Flagged extends Inline {
  static create(value) {
    let node = super.create(value)
    node.setAttribute('class', Flagged.className)
    return node
  }

  static formats(node) {
    const word = node.innerText.toLowerCase()
    const replacement = BAD_TRANSLATION_WORDS[word]
    if (!node.hasAttribute('tooltip'))
      node.setAttribute(
        'tooltip',
        `This word is known to translate poorly.${replacement ? ` Try "${replacement}"` : ''}`
      )
    return true
  }

  optimize(context) {
    super.optimize(context)
    const text = this.domNode.innerText.toLowerCase()
    const trimmed = text.trim()
    const trailingSpace = text.split(trimmed)[1]

    if (!(trimmed in BAD_TRANSLATION_WORDS)) {
      this.unwrap()
    }
    // this makes the format not get expanded when you press space after it, but
    // this would need to change the selection. I cant figure out how to access the quill instance...
    // else if (trailingSpace !== '') {
    //   this.split(trimmed.length).unwrap()
    // }
  }
}
Flagged.blotName = 'flagged'
Flagged.tagName = 'span'
Flagged.className = 'flagged-word'
Quill.register(Flagged)

// Link.sanitize = function (url) {
//   if (!url.includes('http://') && !url.includes('https://')) return 'https://' + url
//   return url
// }

const Clipboard = Quill.import('modules/clipboard')
class CustomClipboard extends Clipboard {
  onCopy(range: Range, isCut: boolean) {
    // Custom getText to include message scripts =====
    const text = this.quill.editor
      .getContents(range.index, range.length)
      .map((op) => {
        if (typeof op.insert === 'string') return op.insert
        // take message-script without guard nodes &#xFEFF;
        if (op.insert['message-script']) return op.insert['message-script'].replace(/&#xFEFF;/g, '')
        return ''
      })
      .join('')
    // ===============================================
    const html = this.quill.getSemanticHTML(range)
    return { html, text }
  }
}
Quill.register('modules/clipboard', CustomClipboard, true)
Quill.register({
  'modules/magicUrl': MagicUrl,
})

export type QuillFormat = 'bold' | 'italic' | 'link' | 'list' | 'message-script' | 'flagged'

const randomUUID = () => {
  return 'xxxxxxxx'.replace(/[xy]/g, function (c) {
    return ((Math.random() * 16) | 0).toString(16)
  })
}

export const getQuillModules = (formats: QuillFormat[]) => {
  let modules = {
    toolbar: toolbarFromFormats(formats),
    magicUrl: {},
  }
  return modules
}

export const toolbarFromFormats = (formats: QuillFormat[]) => {
  let toolbar = []
  formats.forEach((format) => {
    switch (format) {
      case 'bold':
        toolbar.push({ bold: true })
        break
      case 'italic':
        toolbar.push({ italic: true })
        break
      case 'link':
        toolbar.push({ link: true })
        break
      case 'list':
        toolbar.push({ list: 'bullet' })
        break
    }
  })
  return toolbar
}

const GENERIC_IMAGE_NAMES = ['image', 'screenshot', 'paste', 'clip', 'untitled']

export const initializeWithFormats = (
  quill,
  formats: QuillFormat[],
  tags: string[],
  flagPoorTranslations = false,
  imageHandler?: (file: File) => void,
  maxLength = 100000
) => {
  // to avoid issues with react strict mode
  if (quill.alreadyInitialized) return
  quill.alreadyInitialized = true

  addColorRemover(quill)
  if (formats.includes('link')) {
    addLinkShorten(quill)
    addLinkTooltip(quill)
    changeTooltipLinkExample(quill)
  }

  addMaxLength(quill, maxLength)

  if (imageHandler) addImagePasteHandler(quill, imageHandler)
  // the order here is important so that message script paste watchers run before flagged words wo
  if (flagPoorTranslations) addFlaggedWordsWatcher(quill)
  if (tags.length > 0) addMessageScriptWatcher(quill, tags)
}

const addMaxLength = (quill, maxLength) => {
  quill.on('text-change', () => {
    const length = quill.getLength()
    if (length > maxLength) quill.deleteText(maxLength, length - maxLength, 'user')
  })
}

const addImagePasteHandler = (quill, imageHandler) =>
  quill.root.addEventListener('paste', (e) => {
    const clipboardData = e.clipboardData || window.clipboardData
    if (!clipboardData) return
    const items = clipboardData.items
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file && file instanceof File) {
          const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.')
          if (GENERIC_IMAGE_NAMES.includes(nameWithoutExtension.toLowerCase())) {
            const extension = file.name.split('.').pop()
            const newFile = new File([file], `image${randomUUID()}.${extension}`, { type: file.type })
            imageHandler(newFile)
          } else {
            imageHandler(file)
          }
        }
      }
    }
  })

export const addColorRemover = (quill) => {
  quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
    delta.forEach((e) => {
      if (e.attributes) {
        e.attributes.color = ''
        e.attributes.background = ''
      }
    })
    return delta
  })
}

export const addLinkShorten = (quill) => {
  quill.on('text-change', () => {
    const delta = quill.getContents()
    const sel = quill.getSelection()
    if (!sel) return
    let currentSelection = sel.index
    let modified = false
    delta.ops.forEach((op, index) => {
      if (op.attributes && op.attributes.link) {
        const linkText = op.insert
        if (typeof linkText === 'string' && linkText.length > 50) {
          delta.ops[index].insert = linkText.slice(0, 25) + '...'
          currentSelection -= linkText.length - 28
          modified = true
        }
      }
    })
    if (modified) {
      quill.setContents(delta)
      quill.setSelection(currentSelection >= 0 ? currentSelection : quill.getLength())
    }
  })
}

export const addLinkTooltip = (quill) => {
  quill.on('text-change', () => {
    for (const a of quill.root.getElementsByTagName('a')) a.setAttribute('tooltip', a.href)
  })
}

export const changeTooltipLinkExample = (quill, example = 'https://example.com') => {
  var tooltip = quill.theme.tooltip
  if (tooltip) {
    var input = tooltip.root.querySelector('input[data-link]')
    input.dataset.link = example
  }
}

export const addFlaggedWordsWatcher = (quill) => {
  assert(quill.options.formats.includes('flagged'), 'flagged format must be included in quill options')

  const flaggedRegex = new RegExp(
    Object.keys(BAD_TRANSLATION_WORDS)
      .map((w) => '\\b' + w + '\\b')
      .join('|'),
    'gi'
  )
  setupTypeListener(quill, (text, leafIndex) => {
    setTimeout(() => {
      // timeout so this always occurs after the message-script processer
      const matches = [...text.matchAll(flaggedRegex)]
      if (matches.length === 0) return
      matches.forEach((match) => {
        quill.formatText(leafIndex + match.index, match[0].length, 'flagged', true)
      })
    })
  })

  // THIS ONLY MATCHES PASTED HTML, WE NEED A NEW WAY TO MATCH PURE TEXT
  quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) =>
    applyFormatToMatches(node, (newDelta, match) => newDelta.insert(match[0], { flagged: true }), flaggedRegex, delta)
  )
}

export const addMessageScriptWatcher = (quill, tags) => {
  assert(quill.options.formats.includes('message-script'), 'message-script format must be included in quill options')

  const possibleTabs = tags.map((t) => t.replace('.', '\\.').replace('*', '[\\w\\s]+'))
  const messageScriptRegex = new RegExp(possibleTabs.map((t) => `\\[${t}\\]`).join('|'), 'g')

  // THIS ONLY MATCHES PASTED HTML, WE NEED A NEW WAY TO MATCH PURE TEXT
  quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) =>
    applyFormatToMatches(
      node,
      (newDelta, match) => newDelta.insert({ 'message-script': match[0] }),
      messageScriptRegex,
      delta
    )
  )
  setupTypeListener(quill, (text, leafIndex) => {
    const matches = [...text.matchAll(messageScriptRegex)]
    if (matches.length === 0) return
    matches.forEach((match) => {
      quill.deleteText(leafIndex + match.index, match[0].length)
      quill.insertEmbed(leafIndex + match.index, 'message-script', match[0])
    })
  })
}

/**
 * Applies a format to all matches of a regex in a text node
 * @param node a text node to format
 * @param originalDelta the original delta accompanying the text node
 * @param format a function that applies the format to the delta
 * @param regex the regex to find regions to format with
 * @returns a new delta with the format applied
 */
const applyFormatToMatches = (node, format, regex, originalDelta) => {
  const text = node.data
  const matches = [...text.matchAll(regex)]
  if (matches.length === 0) return originalDelta
  matches.sort((a, b) => a.index - b.index)
  let index = 0
  const newDelta = new Delta()
  for (const match of matches) {
    const before = text.slice(index, match.index)
    if (before) newDelta.insert(before)
    format(newDelta, match)
    index = match.index + match[0].length
  }
  const after = text.slice(index)
  if (after) newDelta.insert(after)
  return newDelta
}

/**
 * Calls a callback with the last text node whenever a whitespace character is inserted at the end of it
 * @param quill
 * @param checkText a callback that is called when a whitespace character is inserted
 */
const setupTypeListener = (quill, checkText) => {
  quill.on('text-change', (delta) => {
    const ops = delta.ops
    // Only return true, if last operation includes whitespace inserts
    // Equivalent to listening for enter, tab or space
    if (!ops || ops.length < 1 || ops.length > 2) return
    const lastOp = ops[ops.length - 1]
    if (!lastOp.insert || typeof lastOp.insert !== 'string' || !lastOp.insert.match(/\s/)) return
    const triggeredByInlineWhitespace = !!lastOp.insert.match(/ |\t/)
    const sel = quill.getSelection()
    if (!sel) return
    const [leaf] = quill.getLeaf(sel.index)
    const leafIndex = quill.getIndex(leaf)
    if (!leaf.text) return

    // We only care about the leaf until the current cursor position
    const relevantLength = sel.index - leafIndex
    const text: string = leaf.text.slice(0, relevantLength)

    // console.log(leaf.parent)
    if (!text || leaf.parent.domNode.localName === 'a') return

    const nextLetter = leaf.text[relevantLength]
    // Do not proceed if we are in the middle of a word
    if (nextLetter != null && nextLetter.match(/\S/)) return

    const bailOutEndingRegex = triggeredByInlineWhitespace ? /\s\s$/ : /\s$/
    if (text.match(bailOutEndingRegex)) return

    checkText(text, leafIndex)
  })
}

export const insertDeltasAtCursor = (quill: Quill, deltas: DeltaStatic['ops']) => {
  const index = quill.getSelection(true)?.index ?? quill.getLength() - 1
  const delta = {
    ops: [{ retain: index }, ...deltas],
  }
  quill.updateContents(delta, 'user')
  quill.setSelection(index + deltas.length, 0, 'user')
}
